import { supabase } from '../../lib/supabase';

export interface JobApplicationData {
  userId: string;
  jobTitle: string;
  companyName: string;
  jobUrl: string;
  jobDescription?: string;
  resumeVersionId: string;
  status?: 'saved' | 'applied' | 'interview' | 'offer' | 'rejected';
  savedDate?: string;
  appliedDate?: string;
  notes?: string;
}

export interface JobApplication {
  id: string;
  user_id: string;
  job_title: string;
  company_name: string;
  job_url: string;
  job_description?: string;
  status: string;
  applied_date?: string;
  saved_date?: string;
  reminder_date?: string;
  notes?: string;
  resume_version_id: string;
  created_at: string;
  updated_at: string;
}

export async function saveJobApplication(
  data: JobApplicationData
): Promise<{ success: boolean; data?: JobApplication; error?: string }> {
  try {
    const { data: application, error } = await supabase
      .from('ggj_job_applications')
      .insert({
        user_id: data.userId,
        job_title: data.jobTitle,
        company_name: data.companyName,
        job_url: data.jobUrl,
        resume_version_id: data.resumeVersionId,
        status: data.status || 'saved',
        job_saved_date: data.savedDate || new Date().toISOString(),
        applied_date: data.appliedDate || null,
        notes: data.notes || null,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      })
      .select()
      .single();

    if (error) {
      console.error('Error saving job application:', error);
      return { success: false, error: error.message };
    }

    return { success: true, data: application };
  } catch (error: any) {
    console.error('Unexpected error saving job application:', error);
    return { success: false, error: error.message || 'Unknown error' };
  }
}

export async function updateJobApplication(
  applicationId: string,
  updates: Partial<JobApplicationData>
): Promise<{ success: boolean; data?: JobApplication; error?: string }> {
  try {
    const updateData: any = {
      ...updates,
      updated_at: new Date().toISOString()
    };

    const { data: application, error } = await supabase
      .from('ggj_job_applications')
      .update(updateData)
      .eq('id', applicationId)
      .select()
      .single();

    if (error) {
      console.error('Error updating job application:', error);
      return { success: false, error: error.message };
    }

    return { success: true, data: application };
  } catch (error: any) {
    console.error('Unexpected error updating job application:', error);
    return { success: false, error: error.message || 'Unknown error' };
  }
}

export async function getUserJobApplications(
  userId: string
): Promise<{ success: boolean; data?: JobApplication[]; error?: string }> {
  try {
    const { data: applications, error } = await supabase
      .from('ggj_job_applications')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching job applications:', error);
      return { success: false, error: error.message };
    }

    return { success: true, data: applications || [] };
  } catch (error: any) {
    console.error('Unexpected error fetching job applications:', error);
    return { success: false, error: error.message || 'Unknown error' };
  }
}

export async function deleteJobApplication(
  applicationId: string
): Promise<{ success: boolean; error?: string }> {
  try {
    const { error } = await supabase
      .from('ggj_job_applications')
      .delete()
      .eq('id', applicationId);

    if (error) {
      console.error('Error deleting job application:', error);
      return { success: false, error: error.message };
    }

    return { success: true };
  } catch (error: any) {
    console.error('Unexpected error deleting job application:', error);
    return { success: false, error: error.message || 'Unknown error' };
  }
}
